import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function AmcManager() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("active"); 
  
  const [contracts, setContracts] = useState([]); 
  const [expiredMachines, setExpiredMachines] = useState([]); 
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // 🔥 DATA FOR DROPDOWNS
  const [allInstallations, setAllInstallations] = useState([]); // Raw Data
  const [uniqueHospitals, setUniqueHospitals] = useState([]); // List of Hospital Names
  const [availableSerials, setAvailableSerials] = useState([]); // Filtered Serials based on selected Hospital
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
      serialNo: "", hospital: "", address: "", city: "", contact: "", email: "",
      type: "AMC", startDate: "", endDate: "", amount: "", 
      pmsFrequency: "4", amcBy: "", 
      notes: "" // Notes Field
  });

  // 1. FETCH DATA
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line
  }, []);

  const fetchAllData = async () => {
      setLoading(true);
      try {
          // A. Fetch Active AMC Contracts
          const amcSnap = await getDocs(collection(db, "amc_contracts"));
          const amcList = amcSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          
          // B. Fetch All Installations
          const installSnap = await getDocs(collection(db, "installations"));
          const allMachines = installSnap.docs.map(d => d.data());
          setAllInstallations(allMachines); 

          // 🔥 EXTRACT UNIQUE HOSPITALS
          // We check 'hospitalName' first, then 'hospital'
          const hospitals = [...new Set(allMachines.map(m => m.hospitalName || m.hospital))].filter(h => h);
          setUniqueHospitals(hospitals.sort());

          // Filter: Warranty Expired AND Not in AMC List
          const activeSerialNumbers = amcList.map(a => a.serialNo); 
          
          const expiredList = allMachines.filter(m => {
              const isWarrantyOver = new Date(m.warrantyExp) < new Date(); 
              const hasActiveAMC = activeSerialNumbers.includes(m.serialNo); 
              return isWarrantyOver && !hasActiveAMC;
          });

          setContracts(amcList);
          setExpiredMachines(expiredList);
          setFiltered(activeTab === 'active' ? amcList : expiredList);

      } catch (e) {
          console.error("Error fetching data:", e);
      }
      setLoading(false);
  };

  // 2. SEARCH FILTER
  useEffect(() => {
      let source = activeTab === 'active' ? contracts : expiredMachines;
      if(searchTerm) {
          const lower = searchTerm.toLowerCase();
          const res = source.filter(i => 
              (i.serialNo || "").toLowerCase().includes(lower) || 
              (i.hospital || "").toLowerCase().includes(lower) ||
              (i.city || "").toLowerCase().includes(lower)
          );
          setFiltered(res);
      } else {
          setFiltered(source);
      }
  }, [searchTerm, activeTab, contracts, expiredMachines]);

  // 🔥 3. HANDLE HOSPITAL SELECTION (Updated & Smart Logic)
  const handleHospitalSelect = (e) => {
      const selectedHospital = e.target.value;
      
      // 1. Filter Machines for this Hospital (Check both field names)
      const hospitalMachines = allInstallations.filter(i => 
          (i.hospitalName && i.hospitalName.trim() === selectedHospital.trim()) || 
          (i.hospital && i.hospital.trim() === selectedHospital.trim())
      );

      setAvailableSerials(hospitalMachines);

      // 2. Smart Auto-Fill Logic (Check multiple spellings)
      let autoFillData = {
          address: "",
          city: "",
          contact: "",
          email: ""
      };

      if (hospitalMachines.length > 0) {
          const info = hospitalMachines[0]; // Pick data from first machine
          
          autoFillData = {
              address: info.address || info.Address || info.location || info.Location || info.place || "",
              city: info.city || info.City || info.district || info.District || "",
              contact: info.contact || info.Contact || info.mobile || info.Mobile || info.phone || "",
              email: info.email || info.Email || ""
          };
      }

      // 3. Update State
      setFormData(prev => ({
          ...prev,
          hospital: selectedHospital,
          serialNo: "", // Reset Serial
          ...autoFillData // Apply Auto-fill
      }));
  };

  // 🔥 4. HANDLE SERIAL SELECTION
  const handleSerialSelect = (e) => {
      setFormData(prev => ({ ...prev, serialNo: e.target.value }));
  };

  // 5. SAVE CONTRACT
  const handleSave = async () => {
      if(!formData.serialNo || !formData.endDate || !formData.hospital) return alert("Please fill mandatory fields!");
      
      setLoading(true);
      await addDoc(collection(db, "amc_contracts"), {
          ...formData,
          status: "Active",
          createdAt: new Date().toISOString()
      });
      alert("AMC Contract Created Successfully! ✅");
      setShowModal(false);
      
      // Reset
      setFormData({ serialNo: "", hospital: "", address: "", city: "", contact: "", email: "", type: "AMC", startDate: "", endDate: "", amount: "", pmsFrequency: "4", amcBy: "", notes: "" });
      fetchAllData(); 
  };

  // 6. EXCEL DOWNLOAD
  const downloadExcel = () => {
      const dataToExport = filtered.map(i => ({
          Serial_No: i.serialNo,
          Hospital: i.hospital,
          City: i.city,
          Type: i.type,
          End_Date: activeTab === 'active' ? i.endDate : i.warrantyExp,
          Notes: i.notes || "-",
          Amount: i.amount || "-",
          AMC_By: i.amcBy || "-"
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AMC_Data");
      XLSX.writeFile(wb, `AMC_Report.xlsx`);
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB') : "-";
  
  const convertToAmc = (machine) => {
      // Pre-fill logic for conversion
      const hospitalName = machine.hospitalName || machine.hospital;
      
      // Prepare dropdown list for this hospital
      const hospitalMachines = allInstallations.filter(i => (i.hospitalName === hospitalName || i.hospital === hospitalName));
      setAvailableSerials(hospitalMachines);

      setFormData({
          serialNo: machine.serialNo,
          hospital: hospitalName,
          address: machine.address || machine.Address || "",
          city: machine.city || machine.City || "",
          contact: machine.contact || machine.mobile || "",
          email: machine.email || "",
          type: "AMC", startDate: new Date().toISOString().split('T')[0], endDate: "", amount: "", pmsFrequency: "4", amcBy: "", notes: ""
      });
      
      setShowModal(true);
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif"}}>
      
      {/* HEADER */}
      <div style={headerStyle}>
          <h2 style={{margin:0, color:"#2c3e50"}}>🛠️ AMC & Warranty Manager</h2>
          <div style={{display:"flex", gap:"10px"}}>
              <button onClick={downloadExcel} style={btnDownload}>📥 Excel</button>
              <button onClick={()=>{
                  setFormData({serialNo:"", hospital:"", type:"AMC", pmsFrequency:"4", notes:""}); 
                  setAvailableSerials([]); // Reset dropdown
                  setShowModal(true);
              }} style={btnAdd}>➕ New Contract</button>
          </div>
      </div>

      {/* TABS & SEARCH */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", background:"white", padding:"10px", borderRadius:"8px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)"}}>
          <div style={{display:"flex", gap:"5px"}}>
              <button onClick={()=>setActiveTab("active")} style={activeTab==='active' ? tabActive : tabInactive}>📜 Active Contracts</button>
              <button onClick={()=>setActiveTab("expired")} style={activeTab==='expired' ? tabActive : tabInactive}>⚠️ Expired Warranty (Leads)</button>
          </div>
          <input type="text" placeholder="🔍 Search Serial, Hospital..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={inputStyle} />
      </div>

      {loading ? <Loader /> : (
          <div style={{overflowX:"auto", background:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
              <table style={tableStyle}>
                  <thead>
                      <tr style={{background:"#34495e", color:"white"}}>
                          <th style={thStyle}>Machine & Serial</th>
                          <th style={thStyle}>Hospital Details</th>
                          <th style={thStyle}>{activeTab==='active' ? 'Contract Duration' : 'Warranty Expired On'}</th>
                          <th style={thStyle}>Notes / PMS</th>
                          <th style={thStyle}>Action</th>
                      </tr>
                  </thead>
                  <tbody>
                      {filtered.length === 0 && <tr><td colSpan="5" style={{padding:"20px", textAlign:"center"}}>No Data Found</td></tr>}
                      
                      {filtered.map((i, idx) => (
                          <tr key={idx} style={{borderBottom:"1px solid #eee"}}>
                              <td style={tdStyle}>
                                  <span style={{fontWeight:"bold", color:"#2c3e50"}}>{i.serialNo}</span><br/>
                                  <small style={{color:"gray"}}>{i.type}</small>
                              </td>
                              <td style={tdStyle}>
                                  <strong>{i.hospital || i.hospitalName}</strong>
                                  <div style={{fontSize:"11px", color:"#555"}}>
                                      📍 {i.city} | 📞 {i.contact}
                                  </div>
                              </td>
                              <td style={tdStyle}>
                                  {activeTab === 'active' ? (
                                      <span>{formatDate(i.startDate)} ➝ <strong>{formatDate(i.endDate)}</strong></span>
                                  ) : (
                                      <span style={{color:"red", fontWeight:"bold"}}>{formatDate(i.warrantyExp)}</span>
                                  )}
                              </td>
                              <td style={tdStyle}>
                                  {activeTab === 'active' ? (
                                      <div>
                                          {i.notes && <div style={{fontSize:"11px", fontStyle:"italic", color:"#e67e22"}}>📝 {i.notes}</div>}
                                          <div style={{fontSize:"11px"}}>PMS: <strong>{i.pmsFrequency}</strong>/Yr</div>
                                          <div style={{fontSize:"10px", color:"blue"}}>By: {i.amcBy}</div>
                                      </div>
                                  ) : (
                                      <span style={badgeExpired}>Out of Warranty</span>
                                  )}
                              </td>
                              <td style={tdStyle}>
                                  {activeTab === 'active' ? (
                                      <button onClick={async()=>{
                                          if(window.confirm("Delete Contract?")) {
                                              await deleteDoc(doc(db, "amc_contracts", i.id));
                                              fetchAllData();
                                          }
                                      }} style={btnDelete}>🗑️</button>
                                  ) : (
                                      <button onClick={()=>convertToAmc(i)} style={btnConvert}>🔄 Convert</button>
                                  )}
                              </td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      )}

      {/* MODAL FORM */}
      {showModal && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                  <h3 style={{margin:"0 0 15px 0", color:"#2c3e50"}}>📝 {activeTab==='expired'?'Convert to AMC':'New Contract'}</h3>
                  
                  <div style={scrollForm}>
                      
                      {/* 1. SELECT HOSPITAL */}
                      <label style={label}>1. Select Organization (Hospital)</label>
                      <input 
                          list="hospitalOptions" 
                          value={formData.hospital} 
                          onChange={handleHospitalSelect} 
                          style={{...input, border:"2px solid #3498db", background:"#f0f9ff"}} 
                          placeholder="Start typing hospital name..." 
                      />
                      <datalist id="hospitalOptions">
                          {uniqueHospitals.map((h, index) => (
                              <option key={index} value={h} />
                          ))}
                      </datalist>

                      {/* 2. SELECT SERIAL */}
                      <div style={row}>
                          <div style={col}>
                              <label style={label}>2. Select Machine (Serial)</label>
                              <select 
                                  value={formData.serialNo} 
                                  onChange={handleSerialSelect} 
                                  style={{...input, fontWeight:"bold"}}
                                  disabled={!formData.hospital}
                              >
                                  <option value="">-- Select Machine --</option>
                                  {availableSerials.map((m, idx) => (
                                      <option key={idx} value={m.serialNo}>
                                          {m.serialNo} ({m.model || 'Machine'})
                                      </option>
                                  ))}
                              </select>
                          </div>
                          <div style={col}>
                              <label style={label}>Type</label>
                              <select value={formData.type} onChange={(e)=>setFormData({...formData, type:e.target.value})} style={input}>
                                  <option>AMC</option><option>CMC</option>
                              </select>
                          </div>
                      </div>

                      {/* AUTO FILLED DETAILS */}
                      <div style={row}>
                          <div style={col}>
                              <label style={label}>City</label>
                              <input type="text" value={formData.city} onChange={(e)=>setFormData({...formData, city:e.target.value})} style={input} />
                          </div>
                          <div style={col}>
                              <label style={label}>Address</label>
                              <input type="text" value={formData.address} onChange={(e)=>setFormData({...formData, address:e.target.value})} style={input} />
                          </div>
                      </div>
                      
                      {/* Contact Info */}
                      <div style={row}>
                          <div style={col}>
                              <label style={label}>Contact No</label>
                              <input type="text" value={formData.contact} onChange={(e)=>setFormData({...formData, contact:e.target.value})} style={input} />
                          </div>
                          <div style={col}>
                              <label style={label}>Email</label>
                              <input type="text" value={formData.email} onChange={(e)=>setFormData({...formData, email:e.target.value})} style={input} />
                          </div>
                      </div>

                      <div style={row}>
                          <div style={col}>
                              <label style={label}>Start Date</label>
                              <input type="date" value={formData.startDate} onChange={(e)=>setFormData({...formData, startDate:e.target.value})} style={input} />
                          </div>
                          <div style={col}>
                              <label style={label}>End Date</label>
                              <input type="date" value={formData.endDate} onChange={(e)=>setFormData({...formData, endDate:e.target.value})} style={input} />
                          </div>
                      </div>

                      <div style={row}>
                          <div style={col}>
                              <label style={label}>Total Amount (₹)</label>
                              <input type="number" value={formData.amount} onChange={(e)=>setFormData({...formData, amount:e.target.value})} style={input} />
                          </div>
                          <div style={col}>
                              <label style={label}>PMS Frequency</label>
                              <input type="number" value={formData.pmsFrequency} onChange={(e)=>setFormData({...formData, pmsFrequency:e.target.value})} style={input} placeholder="e.g. 4" />
                          </div>
                      </div>

                      <label style={label}>AMC Brought By (Name)</label>
                      <input type="text" value={formData.amcBy} onChange={(e)=>setFormData({...formData, amcBy:e.target.value})} style={input} placeholder="Sales Person Name" />

                      {/* 🔥 NOTES SECTION */}
                      <label style={{...label, color:"#e67e22"}}>📝 Notes / Promises / Freebies</label>
                      <textarea 
                          value={formData.notes} 
                          onChange={(e)=>setFormData({...formData, notes:e.target.value})} 
                          style={{...input, height:"60px", border:"1px solid #f39c12"}} 
                          placeholder="e.g. Free 1 service, Stabilizer provided..."
                      ></textarea>

                  </div>

                  <div style={{marginTop:"15px", textAlign:"right", borderTop:"1px solid #eee", paddingTop:"10px"}}>
                      <button onClick={()=>setShowModal(false)} style={btnCancel}>Cancel</button>
                      <button onClick={handleSave} style={btnSave}>Save Contract</button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}

// STYLES
const headerStyle={display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px"};
const inputStyle={padding:"8px 12px", borderRadius:"5px", border:"1px solid #ccc", width:"250px"};
const btnAdd={padding:"8px 15px", background:"#2c3e50", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontSize:"13px"};
const btnDownload={padding:"8px 15px", background:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", marginRight:"10px", fontSize:"13px"};

const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px", minWidth:"800px"};
const thStyle={padding:"12px", textAlign:"left", borderLeft:"1px solid #555"};
const tdStyle={padding:"10px 12px", verticalAlign:"middle"};

const tabActive={padding:"8px 15px", background:"#3498db", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", marginRight:"5px", fontWeight:"bold"};
const tabInactive={padding:"8px 15px", background:"#ecf0f1", color:"#7f8c8d", border:"none", borderRadius:"5px", cursor:"pointer", marginRight:"5px"};

const btnDelete={background:"#ffebee", border:"none", cursor:"pointer", padding:"5px 10px", borderRadius:"4px", fontSize:"14px"};
const btnConvert={background:"#27ae60", color:"white", border:"none", cursor:"pointer", padding:"6px 12px", borderRadius:"4px", fontSize:"12px", fontWeight:"bold"};
const badgeExpired={background:"#ffebee", color:"#c0392b", padding:"4px 8px", borderRadius:"4px", fontSize:"11px", fontWeight:"bold"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"10px", width:"500px", boxShadow:"0 5px 15px rgba(0,0,0,0.3)"};
const scrollForm={maxHeight:"70vh", overflowY:"auto", paddingRight:"5px"};
const row={display:"flex", gap:"10px", marginBottom:"5px"};
const col={flex:1};
const input={width:"100%", padding:"8px", margin:"5px 0 15px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const label={fontSize:"12px", fontWeight:"bold", color:"#555"};
const btnSave={padding:"10px 20px", background:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", marginLeft:"10px", fontWeight:"bold"};
const btnCancel={padding:"10px 20px", background:"#95a5a6", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};

export default AmcManager;